
import React, { useEffect, useState } from 'react';
import {
  NavLink,
  useLocation,
  useNavigate
} from 'react-router-dom';
import './NavMenu.css';

// Small reusable navigation icons
function NavIcon({ name }) {
  const paths = {
    home: (
      <>
        <path d="M3 10.5 12 3l9 7.5" />
        <path d="M5 9.5V21h14V9.5" />
        <path d="M9 21v-7h6v7" />
      </>
    ),
    explore: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18" />
        <path d="M12 3c-5 5-5 13 0 18" />
        <path d="M12 3c5 5 5 13 0 18" />
      </>
    ),
    favorites: (
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z" />
    ),
    lists: (
      <>
        <path d="M3 7a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v10H3Z" />
        <path d="M3 10h18" />
      </>
    ),
    profile: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21a8 8 0 0 1 16 0" />
      </>
    ),
    login: (
      <>
        <path d="M10 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h5" />
        <path d="M14 7l5 5-5 5" />
        <path d="M19 12H8" />
      </>
    ),
    logout: (
      <>
        <path d="M10 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h5" />
        <path d="M14 7l5 5-5 5" />
        <path d="M19 12H8" />
      </>
    ),
    signup: (
      <>
        <circle cx="9" cy="8" r="4" />
        <path d="M2 21a7 7 0 0 1 14 0" />
        <path d="M19 8v8" />
        <path d="M15 12h8" />
      </>
    )
  };

  return (
    <svg
      className="nav-menu__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

const NavMenu = ({
  isAuthenticated,
  setIsAuthenticated
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Remember desktop sidebar preference
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem(
      'faunatrack-sidebar-collapsed'
    ) === 'true';
  });

  // Mobile navigation
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    window.matchMedia('(max-width: 700px)').matches
  );

  // Detect screen size changes
  useEffect(() => {
    const media = window.matchMedia('(max-width: 700px)');

    function handleChange(event) {
      setIsMobile(event.matches);
    }

    media.addEventListener('change', handleChange);

    return () => {
      media.removeEventListener('change', handleChange);
    };
  }, []);

  // Save desktop sidebar preference
  useEffect(() => {
    localStorage.setItem(
      'faunatrack-sidebar-collapsed',
      String(collapsed)
    );
  }, [collapsed]);

  // Close mobile navigation after changing pages
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Notify Mapbox when the available width changes
  useEffect(() => {
    const timer = window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 250);

    return () => window.clearTimeout(timer);
  }, [collapsed, mobileOpen, isMobile]);

  const expanded = isMobile ? mobileOpen : !collapsed;

  function toggleSidebar() {
    if (isMobile) {
      setMobileOpen(previous => !previous);
    } else {
      setCollapsed(previous => !previous);
    }
  }

  function handleLogout() {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setMobileOpen(false);

    // Always return home after logging out
    navigate('/', { replace: true });
  }

  function closeMobileMenu() {
    setMobileOpen(false);
  }

  const navLinkClass = ({ isActive }) =>
    `nav-menu__link ${
      isActive ? 'nav-menu__link--active' : ''
    }`;

  return (
    <aside
      className={[
        'nav-menu',
        collapsed ? 'nav-menu--collapsed' : '',
        mobileOpen ? 'nav-menu--mobile-open' : ''
      ].filter(Boolean).join(' ')}
    >
      {/* Sidebar header */}
      <div className="nav-menu__header">

        <NavLink
          to="/"
          className="nav-menu__brand"
          onClick={closeMobileMenu}
          title="FaunaTrack"
        >
          <span
            className="nav-menu__brand-icon"
            aria-hidden="true"
          >
            🐾
          </span>

          <span className="nav-menu__brand-name">
            FaunaTrack
          </span>
        </NavLink>

        <button
          type="button"
          className="nav-menu__toggle"
          onClick={toggleSidebar}
          aria-label={
            expanded ? 'Collapse menu' : 'Expand menu'
          }
          aria-expanded={expanded}
          aria-controls="faunatrack-navigation"
          title={
            expanded ? 'Collapse menu' : 'Expand menu'
          }
        >
          <span className="nav-menu__hamburger">
            <span />
            <span />
            <span />
          </span>
        </button>
      </div>

      {/* Navigation links */}
      <div
        className="nav-menu__navigation"
        id="faunatrack-navigation"
      >
        <nav
          className="nav-menu__links"
          aria-label="Main navigation"
        >
          <NavLink
            to="/"
            end
            className={navLinkClass}
            onClick={closeMobileMenu}
            title="Home"
            aria-label="Home"
          >
            <NavIcon name="home" />
            <span className="nav-menu__label">
              Home
            </span>
          </NavLink>

          <NavLink
            to="/explore"
            className={navLinkClass}
            onClick={closeMobileMenu}
            title="Explore"
            aria-label="Explore"
          >
            <NavIcon name="explore" />
            <span className="nav-menu__label">
              Explore
            </span>
          </NavLink>

          {/* Always visible, even when logged out */}
          <NavLink
            to="/favorites"
            className={navLinkClass}
            onClick={closeMobileMenu}
            title="Favorites"
            aria-label="Favorites"
          >
            <NavIcon name="favorites" />
            <span className="nav-menu__label">
              Favorites
            </span>
          </NavLink>

          <NavLink
            to="/lists"
            className={navLinkClass}
            onClick={closeMobileMenu}
            title="Lists"
            aria-label="Lists"
          >
            <NavIcon name="lists" />
            <span className="nav-menu__label">
              Lists
            </span>
          </NavLink>

          {isAuthenticated && (
            <NavLink
              to="/profile"
              className={navLinkClass}
              onClick={closeMobileMenu}
              title="Profile"
              aria-label="Profile"
            >
              <NavIcon name="profile" />
              <span className="nav-menu__label">
                Profile
              </span>
            </NavLink>
          )}
        </nav>

        {/* Bottom authentication controls */}
        <div className="nav-menu__footer">

          {isAuthenticated ? (
            <button
              type="button"
              className="nav-menu__link nav-menu__logout"
              onClick={handleLogout}
              title="Logout"
              aria-label="Logout"
            >
              <NavIcon name="logout" />
              <span className="nav-menu__label">
                Logout
              </span>
            </button>
          ) : (
            <>
              <NavLink
                to="/login"
                className={navLinkClass}
                onClick={closeMobileMenu}
                title="Login"
                aria-label="Login"
              >
                <NavIcon name="login" />
                <span className="nav-menu__label">
                  Login
                </span>
              </NavLink>

              <NavLink
                to="/register"
                className={navLinkClass}
                onClick={closeMobileMenu}
                title="Sign Up"
                aria-label="Sign Up"
              >
                <NavIcon name="signup" />
                <span className="nav-menu__label">
                  Sign Up
                </span>
              </NavLink>
            </>
          )}

        </div>
      </div>
    </aside>
  );
};

export default NavMenu;
